import React from "react";

import { CANVAS_DATA } from "@/features/canvas/canvas-constants";
import PageRenderer from "@/features/react-editor/components/PageRenderer";
import { useViewportStore } from "@/features/react-editor/store/viewportStore";
import { Desktop } from "@replo/design-system/components/device-mockup/Desktop";
import { Mobile } from "@replo/design-system/components/device-mockup/Mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import useMeasure from "react-use-measure";

interface DevicePreviewProps {
  pageRoute: string;
  className?: string;
  targetViewport?: "mobile" | "desktop";
  isScrollable?: boolean;
  hideScrollbars?: boolean;
  isInteractive?: boolean;
  publishedSiteUrl?: string;
  domain?: string;
  mobileClassName?: string;
  desktopClassName?: string;
  children?: React.ReactNode;
  maxMobileWidth?: number;
  rendererContainerRef?: React.MutableRefObject<HTMLDivElement | null>;
}

export const DevicePreview: React.FC<DevicePreviewProps> = ({
  pageRoute,
  className,
  targetViewport,
  isScrollable = false,
  hideScrollbars,
  isInteractive = false,
  publishedSiteUrl,
  domain,
  mobileClassName,
  desktopClassName,
  children,
  maxMobileWidth,
  rendererContainerRef,
}) => {
  const { currentViewport } = useViewportStore();
  const viewport = targetViewport ?? currentViewport;
  const [containerRef, containerBounds] = useMeasure({ offsetSize: true });

  const baseWidth =
    viewport === "mobile"
      ? CANVAS_DATA.mobile.defaultFrameWidth
      : CANVAS_DATA.desktop.defaultFrameWidth;

  const frameScale =
    containerBounds.width > 0 ? containerBounds.width / baseWidth : 1;

  if (viewport === "mobile") {
    return (
      <div
        ref={containerRef}
        className="h-full w-full flex items-center justify-center"
        style={{ maxWidth: maxMobileWidth ? `${maxMobileWidth}px` : undefined }}
      >
        <div className="h-full w-full">
          <Mobile className={mobileClassName} frameScale={frameScale}>
            {children ??
              (frameScale > 0 ? (
                <PageRenderer
                  pageRoute={pageRoute}
                  className={className}
                  targetViewport="mobile"
                  isScrollable={isScrollable}
                  hideScrollbars={hideScrollbars}
                  isInteractive={isInteractive}
                  containerRef={rendererContainerRef}
                />
              ) : null)}
          </Mobile>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="h-full w-full">
      <Desktop
        url={publishedSiteUrl ?? domain}
        className={twMerge(
          "h-full w-full max-w-full max-h-full overflow-hidden",
          desktopClassName,
        )}
        frameScale={frameScale}
      >
        {children ?? (
          <PageRenderer
            pageRoute={pageRoute}
            className={className}
            targetViewport="desktop"
            isScrollable={isScrollable}
            hideScrollbars={hideScrollbars}
            isInteractive={isInteractive}
            containerRef={rendererContainerRef}
          />
        )}
      </Desktop>
    </div>
  );
};
